import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, tierLabel, salePrice } from "@/lib/api";
import { toast } from "sonner";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

export const StoreProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [config, setConfig] = useState({});
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem("schok_cart") || "[]"));
  const [wishlist, setWishlist] = useState(() => JSON.parse(localStorage.getItem("schok_wishlist") || "[]"));
  const [authOpen, setAuthOpen] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);

  useEffect(() => { localStorage.setItem("schok_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("schok_wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    const token = localStorage.getItem("schok_token");
    if (token) {
      api.get("/account/me")
        .then((r) => setUser(r.data))
        .catch(() => { localStorage.removeItem("schok_token"); })
        .finally(() => setBootLoading(false));
    } else {
      setBootLoading(false);
    }
  }, []);

  const loginSuccess = useCallback(async (token, u) => {
    localStorage.setItem("schok_token", token);
    setUser(u);
    // merge local wishlist to server
    const local = JSON.parse(localStorage.getItem("schok_wishlist") || "[]");
    try {
      const server = (await api.get("/account/wishlist")).data.map((p) => p.product_id);
      const merged = Array.from(new Set([...server, ...local]));
      for (const id of local) {
        if (!server.includes(id)) await api.post(`/account/wishlist/${id}`);
      }
      setWishlist(merged);
    } catch (e) {
      setWishlist(local); // server sync failed — keep the device wishlist until next sign-in
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("schok_token");
    setUser(null);
    toast.success("Signed out");
  }, []);

  // ---- Cart ----
  const addToCart = useCallback((product, pieces, qty = 1) => {
    setCart((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product.product_id && i.pieces === pieces);
      const price = salePrice(product, pieces);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + qty };
        return copy;
      }
      return [...prev, {
        product_id: product.product_id, name: product.name, slug: product.slug,
        image: (product.images || [])[0], pieces, qty, price,
      }];
    });
    toast.success(`${qty} × ${product.name} (${tierLabel(pieces)}) added to cart`);
  }, []);

  const updateCartQty = useCallback((product_id, pieces, qty) => {
    setCart((prev) => prev.map((i) =>
      i.product_id === product_id && i.pieces === pieces ? { ...i, qty: Math.max(1, qty) } : i));
  }, []);

  const removeFromCart = useCallback((product_id, pieces) => {
    setCart((prev) => prev.filter((i) => !(i.product_id === product_id && i.pieces === pieces)));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  // ---- Wishlist ----
  const inWishlist = useCallback((pid) => wishlist.includes(pid), [wishlist]);

  const toggleWishlist = useCallback(async (pid) => {
    setWishlist((prev) => prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]);
    if (localStorage.getItem("schok_token")) {
      try {
        await api.post(`/account/wishlist/${pid}`);
      } catch (e) {
        setWishlist((prev) => prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]);
        toast.error("Couldn't update your wishlist. Please try again.");
      }
    }
  }, []);

  const value = {
    user, setUser, config, setConfig, bootLoading,
    cart, addToCart, updateCartQty, removeFromCart, clearCart, cartCount, cartSubtotal,
    wishlist, inWishlist, toggleWishlist,
    authOpen, setAuthOpen, loginSuccess, logout,
    isAdmin: user?.role === "admin",
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};
